<template>
  <div class="enrichment-container">
    <div class="controls" v-if="showControls">
      <label class="switch-label">
        <span>X-axis:</span>
        <select v-model="xAxisType" class="axis-select">
          <option value="geneRatio">GeneRatio</option>
          <option value="oddsRatio">log₁₀(Odds Ratio)</option>
        </select>
      </label>
      <button @click="showSaveDialog = true" class="save-button">
        <span class="save-icon">💾</span> Save Image
      </button>
    </div>
    <v-chart 
      ref="chartRef" 
      class="chart" 
      :option="option"
      :style="{ 
        height: chartHeight + 'px',
        width: isExporting && exportWidth ? exportWidth + 'px' : '100%'
      }"
    />
    
    <!-- Export Progress Overlay -->
    <div v-if="isExporting" class="export-overlay">
      <div class="export-message">
        <div class="spinner"></div>
        <span>Preparing export...</span>
      </div>
    </div>
    
    <!-- Save Dialog -->
    <div v-if="showSaveDialog" class="dialog-overlay" @click="showSaveDialog = false">
      <div class="dialog" @click.stop>
        <div class="dialog-header">
          <h3>Save Figure</h3>
          <button @click="showSaveDialog = false" class="close-btn">&times;</button>
        </div>
        <div class="dialog-body">
          <div class="input-group">
            <label>Format:</label>
            <select v-model="saveFormat" class="format-select">
              <option value="png">PNG</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
          <div class="input-group">
            <label>Width (px):</label>
            <input 
              v-model.number="saveWidth" 
              type="number" 
              min="800" 
              max="5000"
              class="size-input"
            />
          </div>
          <div class="input-group">
            <label>Height (px):</label>
            <input 
              :value="saveHeight" 
              type="number" 
              readonly
              class="size-input readonly-input"
              title="Height is automatically calculated based on number of pathways"
            />
            <small style="color: #999; font-size: 12px;">Auto-calculated</small>
          </div>
        </div>
        <div class="dialog-footer">
          <button @click="showSaveDialog = false" class="cancel-btn">Cancel</button>
          <button @click="saveFigure" class="confirm-btn">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { ScatterChart, CustomChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import { computed, defineProps, defineEmits, ref, onMounted, onBeforeUnmount, watch } from "vue";
import { jsPDF } from "jspdf";

use([
  CanvasRenderer,
  ScatterChart,
  CustomChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
]);

const props = defineProps({
  data: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  showControls: {
    type: Boolean,
    default: true,
  },
  xAxis: {
    type: String,
    default: "geneRatio",
  },
});

const emit = defineEmits(['saved']);

// X-axis type selection - use prop value
const xAxisType = ref(props.xAxis);

// Watch for prop changes
watch(() => props.xAxis, (newValue) => {
  xAxisType.value = newValue;
});

// Save dialog state
const showSaveDialog = ref(false);
const chartRef = ref(null);
const saveFormat = ref('png');
const isExporting = ref(false);
const exportWidth = ref(null);
const exportHeight = ref(null);

// Calculate dimensions based on data
const ROW_HEIGHT = 65; // Fixed row height per pathway (65px)

// Computed dimensions
const chartHeight = computed(() => {
  // If exporting, use export height
  if (isExporting.value && exportHeight.value) {
    return exportHeight.value;
  }
  const pathwaysCount = props.data?.pathways?.length || 10;
  const topMargin = 60; // From grid.top
  const bottomMargin = 80; // From grid.bottom (increased for horizontal legend)
  return pathwaysCount * ROW_HEIGHT + topMargin + bottomMargin;
});

// Save dimensions - use fixed export size
const saveWidth = ref(1000);
const saveHeight = computed(() => {
  const pathwaysCount = props.data?.pathways?.length || 10;
  const topMargin = 60;
  const bottomMargin = 80;
  return pathwaysCount * ROW_HEIGHT + topMargin + bottomMargin;
});

// Save figure function
const saveFigure = () => {
  if (!chartRef.value) {
    console.error('Chart reference not found');
    return;
  }

  if (saveFormat.value === 'pdf') {
    saveFigureAsPDF();
  } else {
    saveFigureAsPNG();
  }
};

// Save as PNG
const saveFigureAsPNG = async () => {
  const finalWidth = saveWidth.value;
  const finalHeight = saveHeight.value;

  // Set exporting state and dimensions
  isExporting.value = true;
  exportWidth.value = finalWidth;
  exportHeight.value = finalHeight;

  // Wait for next tick and chart to resize
  await new Promise(resolve => setTimeout(resolve, 300));

  // Manually trigger chart resize
  if (chartRef.value && typeof chartRef.value.resize === 'function') {
    chartRef.value.resize({ width: finalWidth, height: finalHeight });
  }

  // Wait for chart to fully render at new size
  await new Promise(resolve => setTimeout(resolve, 200));

  const chartInstance = chartRef.value;
  const url = chartInstance.getDataURL({
    type: 'png',
    pixelRatio: 2,
    backgroundColor: '#fff',
    excludeComponents: ['toolbox']
  });

  // Restore original size
  isExporting.value = false;
  exportWidth.value = null;
  exportHeight.value = null;

  // Wait for restore
  await new Promise(resolve => setTimeout(resolve, 100));

  // Resize chart back to original
  if (chartRef.value && typeof chartRef.value.resize === 'function') {
    chartRef.value.resize();
  }

  // Download the image
  const link = document.createElement('a');
  link.download = `enrichment_plot_${finalWidth}x${finalHeight}.png`;
  link.href = url;
  link.click();
  
  showSaveDialog.value = false;
  
  // Emit saved event to trigger data refresh
  emit('saved');
};

// Save as PDF
const saveFigureAsPDF = async () => {
  const finalWidth = saveWidth.value;
  const finalHeight = saveHeight.value;

  // Set exporting state and dimensions
  isExporting.value = true;
  exportWidth.value = finalWidth;
  exportHeight.value = finalHeight;

  // Wait for next tick and chart to resize
  await new Promise(resolve => setTimeout(resolve, 300));

  // Manually trigger chart resize
  if (chartRef.value && typeof chartRef.value.resize === 'function') {
    chartRef.value.resize({ width: finalWidth, height: finalHeight });
  }

  // Wait for chart to fully render at new size
  await new Promise(resolve => setTimeout(resolve, 200));

  const chartInstance = chartRef.value;
  const url = chartInstance.getDataURL({
    type: 'png',
    pixelRatio: 2, // Higher resolution for PDF
    backgroundColor: '#fff',
    excludeComponents: ['toolbox']
  });

  // Restore original size
  isExporting.value = false;
  exportWidth.value = null;
  exportHeight.value = null;

  // Wait for restore
  await new Promise(resolve => setTimeout(resolve, 100));

  // Resize chart back to original
  if (chartRef.value && typeof chartRef.value.resize === 'function') {
    chartRef.value.resize();
  }

  // Convert pixels to mm for PDF (assuming 96 DPI)
  const pxToMm = 0.264583;
  const widthMm = finalWidth * pxToMm;
  const heightMm = finalHeight * pxToMm;

  // Create PDF with custom dimensions
  const pdf = new jsPDF({
    orientation: widthMm > heightMm ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [widthMm, heightMm]
  });

  // Add image to PDF (fill entire page)
  pdf.addImage(url, 'PNG', 0, 0, widthMm, heightMm);
  
  // Download the PDF
  pdf.save(`enrichment_plot_${finalWidth}x${finalHeight}.pdf`);
  
  showSaveDialog.value = false;
  
  // Emit saved event to trigger data refresh
  emit('saved');
};

const option = computed(() => {
  if (!props.data || !props.data.pathways || props.data.pathways.length === 0) {
    return {
      title: {
        text: "No enrichment results",
        left: "center",
        top: "center",
        textStyle: { fontSize: 16, color: "#999" },
      },
    };
  }

  const { pathways, data: plotData, leftMargin, xAxisRanges } = props.data;
  const pathwaysCount = pathways.length;

  // Calculate -log10(pvalue) range for visual map
  // Data format: [gene_ratio, log10_odds_ratio, index, -log10_pvalue, count, size]
  const negLog10Pvals = plotData.map((d) => d[3]);
  const minNegLog10P = Math.min(...negLog10Pvals);
  const maxNegLog10P = Math.max(...negLog10Pvals);

  // Get x-axis range based on current axis type
  const xAxisRange = xAxisRanges && xAxisType.value === "geneRatio" 
    ? xAxisRanges.geneRatio 
    : (xAxisRanges && xAxisRanges.oddsRatio) || null;

  // Determine x-axis value based on selection
  const getXValue = (i) => {
    return xAxisType.value === "geneRatio" ? plotData[i][0] : plotData[i][1];
  };

  // Create series data with reversed y-axis positioning
  const seriesData = pathways.map((p, i) => ({
    name: p,
    value: [
      getXValue(i), // x-axis value (gene_ratio or log10_odds_ratio)
      pathwaysCount - 1 - i, // reversed y position
      plotData[i][3], // -log10(pvalue) (for color)
      plotData[i][4], // count (for tooltip)
    ],
    symbolSize: plotData[i][5] // Pre-calculated size from backend
  }));

  // Calculate grid height to enforce 65px per row
  const gridHeight = pathwaysCount * ROW_HEIGHT;

  return {
    backgroundColor: "#fff",
    title: {
      text: '-log₁₀(p-value)',
      left: 'center',
      top: 0,
      textStyle: {
        fontSize: 13,
        fontWeight: 'bold',
        color: '#333'
      }
    },
    grid: {
      left: leftMargin || 450, // Use dynamic margin from backend, fallback to 450
      right: 100,
      top: 60,
      bottom: 80, // Increased to accommodate horizontal legend at bottom
      height: gridHeight, // Enforce exact grid height based on pathways count
    },
    tooltip: {
      formatter: (p) => {
        const originalIndex = pathwaysCount - 1 - p.value[1];
        const [geneRatio, log10OddsRatio, , negLog10Pval, count] = [
          plotData[originalIndex][0],
          plotData[originalIndex][1],
          plotData[originalIndex][2],
          plotData[originalIndex][3],
          plotData[originalIndex][4],
        ];
        const pval = Math.pow(10, -negLog10Pval);
        const oddsRatio = Math.pow(10, log10OddsRatio);
        return `<b>${p.name}</b><br/>
                GeneRatio: ${geneRatio.toFixed(3)}<br/>
                Odds Ratio: ${oddsRatio.toFixed(2)}<br/>
                P value: ${pval.toExponential(2)}<br/>
                Count: ${count}`;
      },
    },
    /*
    xAxis: {
      name: xAxisType.value === "geneRatio" ? "GeneRatio" : "log₁₀(Odds Ratio)",
      nameLocation: "middle",
      nameGap: 35,
      type: "value",
      axisLine: { lineStyle: { color: "#444" } },
      axisLabel: { fontSize: 13 },
      splitLine: { show: true, lineStyle: { color: "#e0e0e0" } },
    },
    yAxis: {
        type: 'category',
        data: pathways.slice().reverse(),
        axisLabel: { fontSize: 14, margin: 10, align: 'right' },
        // key part ↓↓↓
        axisTick: { show: false },
        splitLine: {
        show: true,
        alignWithLabel: true, // <— ensures grid line goes through label, not between bands
        lineStyle: { color: '#e0e0e0', width: 1 },
        },
    },
    */
    xAxis: {
      name: xAxisType.value === "geneRatio" ? "GeneRatio" : "log₁₀(Odds Ratio)",
      nameLocation: 'middle',
      nameGap: 35,
      type: 'value',
      min: xAxisRange ? xAxisRange[0] : 'dataMin',
      max: xAxisRange ? xAxisRange[1] : 'dataMax',
      axisLine: { show: true, lineStyle: { color: '#444', width: 1.2 } },
      axisTick: { show: true },
      axisLabel: { fontSize: 13 },
      splitLine: { show: true, lineStyle: { color: '#e0e0e0' } },
    },
    yAxis: {
      type: 'category',
      data: pathways.slice().reverse(),
      axisLabel: { fontSize: 14, margin: 10, align: 'right' },
      axisTick: { show: false },
      splitLine: {
        show: true,
        alignWithLabel: true,
        lineStyle: { color: '#e0e0e0', width: 1 },
      },
      // 👇 Add this
      axisLine: { show: true, lineStyle: { color: '#444', width: 1 } },
      boundaryGap: false,  // ensures top/bottom gridlines are drawn
    },
    visualMap: [
      {
        dimension: 2,
        min: minNegLog10P,
        max: maxNegLog10P,
        calculable: false,
        orient: "horizontal",
        left: 'center',
        top: 16,
        text: [`${maxNegLog10P.toFixed(1)}`, `${minNegLog10P.toFixed(1)}`],
        textGap: 10,
        textStyle: { fontSize: 11 },
        formatter: (value) => value.toFixed(1),
        inRange: { color: ["#deebf7", "#6baed6", "#2171b5", "#08306b"] },
        seriesIndex: 0, // Only apply to the scatter series, not the custom legend
      },
    ],
    series: [
      {
        type: "scatter",
        data: seriesData,
        itemStyle: { borderColor: "#555", borderWidth: 0.5 },
      },
    ],
  };
});

// Expose methods to parent (ProjectDetail.vue)
const setXAxisType = (type) => {
  if (type === 'geneRatio' || type === 'oddsRatio') {
    xAxisType.value = type;
  }
};

const openSaveDialog = () => {
  showSaveDialog.value = true;
};

defineExpose({ setXAxisType, openSaveDialog, saveFigure });

// ---------- Resize handling (avoid ResizeObserver loop) ----------
function handleResize() {
  window.requestAnimationFrame(() => {
    if (chartRef.value && typeof chartRef.value.resize === 'function') {
      chartRef.value.resize();
    }
  });
}

onMounted(() => {
  window.addEventListener('resize', handleResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.enrichment-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.controls {
  width: 100%;
  padding: 12px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  flex-shrink: 0;
}

.switch-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.axis-select {
  padding: 6px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;
}

.axis-select:hover {
  border-color: #409eff;
}

.axis-select:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.format-select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;
}

.format-select:hover {
  border-color: #409eff;
}

.format-select:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.chart {
  width: 100%;
  background: #fff;
}

.save-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.save-button:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.save-button:active {
  transform: translateY(0);
}

.save-icon {
  font-size: 13px;
}

/* Dialog styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 400px;
  max-width: 500px;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #333;
}

.dialog-body {
  padding: 24px;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.input-group:last-child {
  margin-bottom: 0;
}

.input-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  min-width: 100px;
}

.size-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.size-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.readonly-input {
  background-color: #f5f5f5;
  color: #666;
  cursor: not-allowed;
}

.readonly-input:focus {
  border-color: #d0d0d0;
  box-shadow: none;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.cancel-btn,
.confirm-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background: #f0f0f0;
  color: #666;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.confirm-btn {
  background: #409eff;
  color: white;
}

.confirm-btn:hover {
  background: #66b1ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

/* Export overlay */
.export-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.export-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 30px 50px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

